import React, { useState, useRef, useEffect } from 'react';
import { signInWithEmailAndPassword } from 'firebase/auth';
import { auth } from '../config/firebase';
import { useNavigate } from 'react-router-dom';

function LoginPage() {
  const [loginType, setLoginType] = useState('student'); // 'student' | 'instructor'
  const [identifier, setIdentifier] = useState(''); // Student ID 或 Instructor ID
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  
  // 管理员入口
  const [longPressStart, setLongPressStart] = useState(null);
  const [longPressProgress, setLongPressProgress] = useState(0);
  const pdaLogoRef = useRef(null);
  const navigate = useNavigate();

  // 处理登录
  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      // TODO: 实现实际的登录逻辑
      // 1. 如果是学员: 发送短信验证码
      // 2. 如果是 Instructor: 使用 Instructor ID + 密码
      
      // 临时: 直接用 Firebase Auth
      const email = loginType === 'student' 
        ? `student${identifier}@jscdl.org` 
        : `instructor${identifier}@jscdl.org`;
      
      await signInWithEmailAndPassword(auth, email, password);
      
      // 登录成功后会由 App.jsx 自动跳转
    } catch (err) {
      setError('登录失败: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  // PDA Logo 长按检测
  useEffect(() => {
    let timer;
    if (longPressStart) {
      timer = setInterval(() => {
        const elapsed = Date.now() - longPressStart;
        const progress = Math.min((elapsed / 10000) * 100, 100); // 10秒
        setLongPressProgress(progress);

        // 震动反馈
        if (elapsed === 5000 || elapsed === 8000) {
          navigator.vibrate?.(50);
        }
        if (elapsed === 9000) {
          navigator.vibrate?.(100);
        }
        
        // 10秒到达，进入管理员界面
        if (progress >= 100) {
          navigator.vibrate?.(200);
          navigate('/admin');
          clearInterval(timer);
        }
      }, 100);
    }
    return () => clearInterval(timer);
  }, [longPressStart, navigate]);

  const handlePdaPress = () => {
    setLongPressStart(Date.now());
  };

  const handlePdaRelease = () => {
    if (longPressProgress < 100) {
      // 未达到10秒，跳转到使用条款
      window.open('https://www.pdaus.org/ny-cdl-app-termsofuse', '_blank');
    }
    setLongPressStart(null);
    setLongPressProgress(0);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-blue-100 flex items-center justify-center p-4">
      <div className="max-w-md w-full">
        {/* Logo 区域 */}
        <div className="text-center mb-8 fade-in">
          <div className="mb-6 flex justify-center items-center gap-8">
            {/* J&S Logo */}
            <a 
              href="https://www.jsdrivingschool.com" 
              target="_blank" 
              rel="noopener noreferrer"
              className="transition-transform hover:scale-110"
            >
              <img 
                src="/js_logo.png" 
                alt="J&S Driving School" 
                className="h-12 w-auto"
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.nextSibling.style.display = 'block';
                }}
              />
              <div style={{display: 'none'}} className="h-12 w-20 bg-gray-200 rounded flex items-center justify-center text-xs">J&S</div>
            </a>

            {/* PDA Logo - 长按进入管理员 */}
            <div className="relative">
              <button
                ref={pdaLogoRef}
                onMouseDown={handlePdaPress}
                onMouseUp={handlePdaRelease}
                onMouseLeave={handlePdaRelease}
                onTouchStart={handlePdaPress}
                onTouchEnd={handlePdaRelease}
                className="transition-transform active:scale-95 focus:outline-none"
              >
                <img 
                  src="/pda_logo.png" 
                  alt="PDA" 
                  className="h-12 w-auto"
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.nextSibling.style.display = 'block';
                  }}
                />
                <div style={{display: 'none'}} className="h-12 w-20 bg-gray-200 rounded flex items-center justify-center text-xs">PDA</div>
              </button>
              
              {/* 长按进度条 */}
              {longPressProgress > 0 && (
                <div className="absolute -bottom-2 left-0 right-0 h-1 bg-gray-200 rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-blue-600 transition-all"
                    style={{ width: `${longPressProgress}%` }}
                  />
                </div>
              )}
            </div>
          </div>

          <h1 className="text-3xl font-bold text-gray-900 mb-2">MyDriveBook</h1>
          <p className="text-gray-600">J&S Driving School Management System</p>
        </div>

        {/* 登录卡片 */}
        <div className="card fade-in">
          {/* 角色切换 */}
          <div className="flex gap-2 mb-6">
            <button
              onClick={() => setLoginType('student')}
              className={`flex-1 py-2 rounded-lg font-medium transition-colors ${
                loginType === 'student'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              Student Login
            </button>
            <button
              onClick={() => setLoginType('instructor')}
              className={`flex-1 py-2 rounded-lg font-medium transition-colors ${
                loginType === 'instructor'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              Instructor Login
            </button>
          </div>

          {/* 登录表单 */}
          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {loginType === 'student' ? 'Student ID' : 'Instructor ID'}
              </label>
              <input
                type="text"
                value={identifier}
                onChange={(e) => setIdentifier(e.target.value)}
                className="input"
                placeholder={loginType === 'student' ? 'Enter your Student ID' : 'Enter your Instructor ID'}
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {loginType === 'student' ? '验证码' : '密码'}
              </label>
              <input
                type={loginType === 'student' ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="input"
                placeholder={loginType === 'student' ? '短信验证码' : '输入密码'}
                required
              />
              {loginType === 'student' && (
                <button
                  type="button"
                  className="text-sm text-blue-600 hover:text-blue-700 mt-1"
                  onClick={() => alert('发送验证码功能待实现')}
                >
                  发送验证码
                </button>
              )}
            </div>

            {error && (
              <div className="bg-red-50 text-red-600 text-sm p-3 rounded-lg">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full btn-primary py-3 flex items-center justify-center"
            >
              {loading ? (
                <>
                  <div className="spinner w-5 h-5 mr-2"></div>
                  登录中...
                </>
              ) : (
                '登录'
              )}
            </button>
          </form>

          {/* 帮助链接 */}
          <div className="mt-6 text-center text-sm text-gray-600">
            <p>Forgot Password? <a href="#" className="text-blue-600 hover:text-blue-700">Contact Office</a></p>
            <p className="mt-2">New Student? <a href="#" className="text-blue-600 hover:text-blue-700">Register Account</a></p>
          </div>
        </div>

        {/* 底部说明 */}
        <p className="text-center text-xs text-gray-500 mt-6">
          © 2026 MyDriveBook - J&S Driving School<br/>
          <span className="text-gray-400">Long press PDA Logo for 10 seconds to enter Staff Admin Panel</span>
        </p>
      </div>
    </div>
  );
}

export default LoginPage;
